/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Images } from "@packages/sdk";
import type { JSX, ReactNode } from "react";
import { forwardRef } from "react";

import {
  constantSemanticColors,
  radius,
  spacing,
} from "../../../../../../global/emotion";
import { stylesOpacity } from "../../../../../../global/emotion/styles/stylesOpacity";
import { stylesOutline } from "../../../../../../global/emotion/styles/stylesOutline";
import { stylesScale } from "../../../../../../global/emotion/styles/stylesScale";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../../types/withAsChild.types";
import type { CSSColorStrict } from "../../../../../utils/colors/types/CSSColorStrict.types";
import { determineChildrenInject } from "../../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../../utils/determineElementFromAsChild";
import { HallowImage } from "../../../../blocks/other/HallowImage/HallowImage";
import type { MaskProps } from "../../../_core/Mask/Mask.emotion";
import { Mask } from "../../../_core/Mask/Mask.emotion";
import { Tag } from "../../../_core/Tag/Tag.emotion";
import { Text } from "../../../_core/Text/Text.emotion";
import { CheckmarkIcon } from "../../../icons/Icon/GeneralIcons";

const hallowElement: HallowElement = "div";

const containerStyle: Interpolation = {
  height: spacing.full,
  position: "relative",
  width: spacing.full,
};
const containerContainerStyle: Interpolation<CSSColorStrict> = (color) => ({
  background: color,
  borderRadius: radius.m,
  height: spacing.full,
  position: "absolute",
  width: spacing.full,
});
const determinedElementStyle: Interpolation = {
  borderRadius: radius.m,
  paddingBottom: "127.21712538%",
  position: "relative",
};
const illustrationImgStyle: Interpolation = {
  WebkitUserDrag: "none",
  borderRadius: spacing.s,
  height: spacing.xxxl,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  userSelect: "none",
  width: spacing.xxxl,
};
const imgContainerStyle: Interpolation = {
  paddingBottom: "86.23853211%",
  position: "relative",
};
const imgStyle: Interpolation<CSSColorStrict> = (color) => ({
  WebkitUserDrag: "none",
  borderColor: color,
  borderStyle: "solid",
  borderTopLeftRadius: spacing.m,
  borderTopRightRadius: spacing.m,
  borderWidth: 8,
  height: spacing.full,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  position: "absolute",
  userSelect: "none",
  width: spacing.full,
});
const infoContainerStyle: Interpolation = {
  bottom: spacing.none,
  color: constantSemanticColors.onColorHighest,
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  paddingBottom: spacing.l,
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  position: "absolute",
  width: spacing.full,
};
const infoTopContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
};
const maskStyle: Interpolation = {
  bottom: -1,
  position: "absolute",
};
const tagJoinedStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  maxWidth: 24,
};
const tagsContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};

export type CampaignLargeMobileProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  activeState?: boolean;
  color: CSSColorStrict;
  description: string;
  focusState?: boolean;
  hoverState?: boolean;
  illustrationImgSrc?: string;
  imgSrc?: Images;
  joined?: boolean;
  tags: string[];
  title: string;
  extraInfo?: ReactNode;
};

export const CampaignLargeMobile = forwardRef<any, CampaignLargeMobileProps>(
  (
    {
      activeState = true,
      asChild = false,
      children,
      color,
      description,
      focusState = true,
      hoverState = true,
      illustrationImgSrc,
      imgSrc,
      joined,
      tags,
      title,
      extraInfo,
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        <div key="container" css={containerContainerStyle(color)}>
          <div css={containerStyle}>
            <div css={imgContainerStyle}>
              <HallowImage
                alt={title}
                src={imgSrc}
                size={"l"}
                css={imgStyle(color)}
              />
              <Mask
                color={color as MaskProps["color"]}
                direction="to top"
                css={maskStyle}
              />
            </div>
            <div css={infoContainerStyle}>
              <div css={infoTopContainerStyle}>
                {illustrationImgSrc && (
                  <img
                    alt={title}
                    src={illustrationImgSrc}
                    css={illustrationImgStyle}
                  />
                )}
                <Text overflow="ellipsis" size="xs" type="title">
                  {title}
                </Text>
                <Text lineClamp={2} size="l" type="title">
                  {description}
                </Text>
              </div>
              <div css={tagsContainerStyle}>
                {tags.map((tag) => (
                  <Tag key={tag} variant="lighten">
                    {tag}
                  </Tag>
                ))}
                {joined && (
                  <Tag variant="lighten" css={tagJoinedStyle}>
                    <CheckmarkIcon.SimpleOn size={12} />
                  </Tag>
                )}
              </div>
              {extraInfo}
            </div>
          </div>
        </div>,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        css={[
          determinedElementStyle,
          activeState ? stylesOpacity.active : null,
          activeState ? stylesOpacity.base : null,
          stylesOutline.base,
          focusState ? stylesOutline.focus : null,
          hoverState ? stylesOutline.hover : null,
          activeState && hoverState ? stylesScale.base : null,
          activeState && hoverState ? stylesScale.m : null,
        ]}
        {...props}
      >
        {determinedChildren}
      </DeterminedElement>
    );
  },
);

CampaignLargeMobile.displayName = "CampaignLargeMobile";
